import { FEATURE_FLAG_DEFINITIONS, type FeatureFlagKey } from 'bc-model';
import { createWithEqualityFn } from 'zustand/traditional';
import { combine, devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { StoreApi } from 'zustand/index';
import { getFeatureFlags, reloadFeatureFlags } from '../featureFlags';

type StateType = {
  featureFlags: { [K in FeatureFlagKey]: boolean };
};

const unloadedState: StateType = {
  featureFlags: {} as { [K in FeatureFlagKey]: boolean },
};

const loadFeatureFlags = async () => {
  await reloadFeatureFlags();
  return getFeatureFlags() as { [K in FeatureFlagKey]: boolean };
};

const featureFlagStoreActions = (set: StoreApi<StateType>['setState']) => {
  return {
    loadFeatureFlags: async () => {
      const featureFlags = await loadFeatureFlags();
      if (featureFlags) {
        set(() => {
          return {
            featureFlags,
          };
        });
      }
    },
  };
};

const resolveFlagValue = (flag: FeatureFlagKey, flagValue: boolean | undefined): boolean => {
  return flagValue ?? FEATURE_FLAG_DEFINITIONS[flag]?.fallbackValue ?? false;
};

export const useFeatureFlagStore = createWithEqualityFn(
  devtools(immer(combine(unloadedState, featureFlagStoreActions)), { name: 'featureFlagStore' })
);

/**
 * Sync read of a boolean flag from store state (for use outside of React).
 * @param flag Flag to check
 * @returns Flag's boolean value
 */
export const getBooleanFlagValue = (flag: FeatureFlagKey): boolean => {
  return resolveFlagValue(flag, useFeatureFlagStore.getState().featureFlags[flag]);
};

/**
 * @param flag Flag to check
 * @returns Flags boolean value
 */
export const useBooleanFlagValue = (flag: FeatureFlagKey): boolean => {
  const flagValue = useFeatureFlagStore((state) => state.featureFlags[flag]);
  return resolveFlagValue(flag, flagValue);
};

export type { StateType };
